import React, { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { Button } from "@mui/material";
import { apiVariable } from "../../../../api/apiVariable";
import useAuth from "../../../../hooks/useAuth";
import StatusSnackbar from "../../../General/StatusSnackbar/StatusSnackbar";
import GenericFormPopup from "../../../Fleet/GenericFormPopup/GenericFormPopup";
import {
  accidentFieldConfig,
  accidentInitialValues,
} from "../../../../formConfigs/Fleet/accidentFieldConfig";
import { getId } from "../../../HR/UpdateEmployeePopup/UpdateEmployeePopup";
import dayjs from "dayjs";
import { fetchOwnerCompanies } from "../../TruckPopup/TruckPopup";
import { CHICAGO_TIMEZONE } from "../../../../utils/dayjsTimezone";

const fetchDrivers = async (token: string | undefined | null) => {
  const { data } = await axios.get(`${apiVariable}/getAllDrivers`, {
    headers: { Authorization: `Bearer ${token}` },
    params: { areActive: true },
  });
  return data.map((driver: any) => ({
    label: `${driver.firstName} ${driver.lastName} | ${driver.phoneNumber}`,
    id: driver.id.toString(),
  }));
};

const fetchTrucks = async (token: string | undefined | null) => {
  const { data } = await axios.get(`${apiVariable}/getAllTrucks`, {
    headers: { Authorization: `Bearer ${token}` },
    params: { areActive: true },
  });
  return data.map((truck: any) => ({
    label: `${truck.unitNumber} | ${truck.make} ${truck.model} ${truck.year}`,
    id: truck.id.toString(),
  }));
};

const fetchTrailers = async (token: string | undefined | null) => {
  const { data } = await axios.get(`${apiVariable}/getAllTrailers`, {
    headers: { Authorization: `Bearer ${token}` },
    params: { areActive: true },
  });
  return data.map((trailer: any) => ({
    label: `${trailer.unitNumber} | ${trailer.make} ${trailer.model} ${trailer.year}`,
    id: trailer.id.toString(),
  }));
};

const fetchDispatchers = async (token: string | undefined | null) => {
  const { data } = await axios.get(`${apiVariable}/getAllDispatchers`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data.map((dispatcher: any) => ({
    label: `${dispatcher.firstName} ${dispatcher.lastName} | ${dispatcher.email}`,
    id: dispatcher.id.toString(),
  }));
};

interface UpdateAccidentPopupProps {
  accident: any;
}

const formatAccidentDateForForm = (date?: string | null) =>
  date ? dayjs.utc(date).tz(CHICAGO_TIMEZONE).format("YYYY-MM-DDTHH:mm") : "";

const UpdateAccidentPopup: React.FC<UpdateAccidentPopupProps> = ({
  accident,
}) => {
  const [open, setOpen] = useState(false);
  const [initialValues, setInitialValues] = useState({
    ...accidentInitialValues,
    date: formatAccidentDateForForm(accident.date) || "",
  });
  const [updateStatus, setUpdateStatus] = useState<{
    status: "idle" | "success" | "error";
    message: string;
  }>({
    status: "idle",
    message: "",
  });

  const { token } = useAuth();
  const queryClient = useQueryClient();

  const { data: drivers } = useQuery({
    queryKey: ["drivers"],
    queryFn: () => fetchDrivers(token),
    enabled: open,
  });

  const { data: trucks } = useQuery({
    queryKey: ["trucks"],
    queryFn: () => fetchTrucks(token),
    enabled: open,
  });

  const { data: trailers } = useQuery({
    queryKey: ["trailers"],
    queryFn: () => fetchTrailers(token),
    enabled: open,
  });

  const { data: dispatchers } = useQuery({
    queryKey: ["dispatchers"],
    queryFn: () => fetchDispatchers(token),
    enabled: open,
  });

  const { data: ownerCompanies } = useQuery({
    queryKey: ["ownerCompanies"],
    queryFn: () => fetchOwnerCompanies(token),
    enabled: open,
  });

  useEffect(() => {
    if (accident) {
      const formattedValues = {
        ...accident,
        date: formatAccidentDateForForm(accident.date) || "",
        driverId: accident.driverId
          ? {
              id: accident.driverId.toString(),
              label: accident.driverName || "Unknown Driver",
            }
          : null,
        truckId: accident.truckId
          ? {
              id: accident.truckId.toString(),
              label: accident.truckUnitNumber || "Unknown Truck",
            }
          : null,
        trailerId: accident.trailerId
          ? {
              id: accident.trailerId.toString(),
              label: accident.trailerNumber || "Unknown Trailer",
            }
          : null,
        dispatcherId: accident.dispatcherId
          ? {
              id: accident.dispatcherId.toString(),
              label: accident.dispatcherName || "Unknown Dispatcher",
            }
          : null,
        ownerCompanyId: accident.ownerCompanyId
          ? {
              id: accident.ownerCompanyId.toString(),
              label: accident.ownerCompanyName || "Unknown Owner Company",
            }
          : null,
      };
      setInitialValues(formattedValues);
    }
  }, [accident]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
  };
  const handleSnackbarClose = () =>
    setUpdateStatus({ status: "idle", message: "" });

  const mutation = useMutation({
    mutationFn: (data: any) =>
      axios.put(`${apiVariable}/accident/${accident.id}`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
    onSuccess: () => {
      setUpdateStatus({
        status: "success",
        message: "Accident updated successfully",
      });
      queryClient.invalidateQueries({ queryKey: ["accidents"] });
      handleClose();
    },
    onError: (error: any) => {
      setUpdateStatus({
        status: "error",
        message: error?.response?.data ?? "Failed to update accident",
      });
    },
  });

  const submitHandler = (data: any) => {
    const finalData = {
      ...data,
      date: data.date
        ? dayjs.tz(data.date, CHICAGO_TIMEZONE).utc().toISOString()
        : null,
      driverId: getId(data.driverId),
      truckId: getId(data.truckId),
      trailerId: getId(data.trailerId),
      dispatcherId: getId(data.dispatcherId),
      ownerCompanyId: getId(data.ownerCompanyId),
    };
    mutation.mutate(finalData);
  };

  const dynamicFieldConfig = React.useMemo(() => {
    return accidentFieldConfig.map((field) => {
      if (field.name === "driverId") {
        return { ...field, options: drivers || [] };
      }
      if (field.name === "truckId") {
        return { ...field, options: trucks || [] };
      }
      if (field.name === "trailerId") {
        return { ...field, options: trailers || [] };
      }
      if (field.name === "dispatcherId") {
        return { ...field, options: dispatchers || [] };
      }
      if (field.name === "ownerCompanyId") {
        return { ...field, options: ownerCompanies || [] };
      }
      return field;
    });
  }, [drivers, trucks, trailers, dispatchers, ownerCompanies]);

  return (
    <>
      <Button variant="contained" onClick={handleOpen}>
        Update
      </Button>

      <GenericFormPopup
        open={open}
        onClose={handleClose}
        title="Update Accident"
        initialValues={initialValues}
        autocompleteData={{
          drivers: drivers || [],
          trucks: trucks || [],
          trailers: trailers || [],
          dispatchers: dispatchers || [],
          ownerCompanies: ownerCompanies || [],
        }}
        formFields={dynamicFieldConfig}
        onSubmit={submitHandler}
        loading={mutation.isPending}
      />

      <StatusSnackbar
        status={updateStatus.status}
        successMessage={
          updateStatus.status === "success" ? updateStatus.message : ""
        }
        errorMessage={
          updateStatus.status === "error" ? updateStatus.message : ""
        }
        handleClose={handleSnackbarClose}
      />
    </>
  );
};

export default UpdateAccidentPopup;
